import { sleep } from './utils';

// ============================================================================
// Import all chat JSON files from data/chat/
// Each file represents a recorded conversation with a unique user prompt.
// To add a new conversation, simply drop a new HAR-recorded JSON file here.
// ============================================================================
import chatAnalyzeCpu from "./data/chat/analyze_cpu.json";
import chatCreateVol from "./data/chat/create_vol.json";
import chatDeleteVol from "./data/chat/delete_vol.json";
import chatWelcome from "./data/chat/welcome.json";
import chatBlockedContent from "./data/chat/blocked_content.json";
import chatErrorApi from "./data/chat/error_api.json";

/**
 * Registry of all imported chat conversation JSON files.
 * Add new imports above and include them in this array.
 * @type {Array<Array<Object>>}
 */
const allChatRecordings = [
    chatAnalyzeCpu,
    chatCreateVol,
    chatDeleteVol,
    chatWelcome,
    chatBlockedContent,
    chatErrorApi
];

// ============================================================================
// Multi-Turn Conversation Parsing
// ============================================================================

/**
 * Checks whether a "send" entry is an actual user message (contains a messages array)
 * as opposed to a ping or other control message.
 * @param {Object} entry - A HAR recording entry.
 * @returns {boolean}
 */
const isUserMessage = (entry) => {
    if (entry.type !== 'send') return false;
    try {
        const parsed = JSON.parse(entry.data);
        return !!(parsed.messages && Array.isArray(parsed.messages));
    } catch {
        return false;
    }
};

/**
 * Extracts the last user message content from a send entry's data.
 * In multi-turn conversations, each send contains the full conversation history,
 * so the last user message is the newest prompt for that turn.
 * @param {Object} sendEntry - A "send" HAR entry.
 * @returns {string|null} - The user's message content, or null.
 */
const extractLastUserContent = (sendEntry) => {
    try {
        const parsed = JSON.parse(sendEntry.data);
        const userMessages = parsed.messages?.filter(m => m.role === 'user') || [];
        const lastUserMsg = userMessages[userMessages.length - 1];
        return lastUserMsg?.content?.toLowerCase()?.trim() || null;
    } catch {
        return null;
    }
};

/**
 * Splits a HAR recording into an array of conversation turns.
 *
 * Each turn contains:
 *   - userPrompt: the user's message that initiates this turn (lowercased)
 *   - receiveMessages: the server responses to replay for this turn
 *
 * The recording is segmented at each "send" entry that contains a messages array.
 * "receive" entries between two such sends belong to the earlier turn.
 * Ping sends (type: "ping") are skipped — they are not turn boundaries.
 *
 * Example for create_vol.json (4 sends):
 *   Turn 0: "create a volume..." → [receive responses for turn 0]
 *   Turn 1: "volume name vol_1..."→ [receive responses for turn 1]
 *   Turn 2: "working env ID..."  → [receive responses for turn 2]
 *   Turn 3: "Yes"                → [receive responses for turn 3]
 *
 * @param {Array} recording - The full HAR-recorded message array.
 * @returns {Array<{userPrompt: string, receiveMessages: Array}>}
 */
const parseConversationTurns = (recording) => {
    const turns = [];
    let currentTurn = null;

    for (const entry of recording) {
        if (isUserMessage(entry)) {
            // Start a new turn — any previous turn is now complete
            if (currentTurn) {
                turns.push(currentTurn);
            }
            currentTurn = {
                userPrompt: extractLastUserContent(entry),
                receiveMessages: []
            };
        } else if (entry.type === 'receive' && currentTurn) {
            currentTurn.receiveMessages.push(entry);
        }
        // Ping sends and other non-user sends are ignored as turn boundaries
    }

    // Push the final turn
    if (currentTurn) {
        turns.push(currentTurn);
    }

    return turns;
};

// ============================================================================
// Conversation Map
// ============================================================================

/**
 * @typedef {Object} ConversationEntry
 * @property {string} initialPrompt - The first user message (key for lookup)
 * @property {Array<{userPrompt: string, receiveMessages: Array}>} turns - Ordered turns
 * @property {boolean} isMultiTurn - Whether this conversation has more than one turn
 */

/**
 * Builds a map of initial user prompt → conversation entry for all chat recordings.
 * Key: lowercased first user message for case-insensitive matching.
 * Value: ConversationEntry with all turns for multi-turn replay.
 * @returns {Map<string, ConversationEntry>}
 */
const buildConversationMap = () => {
    const map = new Map();

    allChatRecordings.forEach((recording) => {
        const turns = parseConversationTurns(recording);
        if (turns.length === 0) return;

        const initialPrompt = turns[0].userPrompt;
        if (!initialPrompt) return;

        const entry = {
            initialPrompt,
            turns,
            isMultiTurn: turns.length > 1
        };

        map.set(initialPrompt, entry);
        console.log(
            `AI Chat: Registered conversation — "${initialPrompt.substring(0, 60)}..." ` +
            `(${turns.length} turn(s), ${turns.reduce((sum, t) => sum + t.receiveMessages.length, 0)} messages)`
        );
    });

    console.log(`AI Chat: ${map.size} conversation(s) loaded`);
    return map;
};

/** @type {Map<string, ConversationEntry>} */
let conversationMap = null;

/** Key used to look up the default fallback conversation (welcome.json) */
const FALLBACK_KEY = extractLastUserContent(chatWelcome.find(e => isUserMessage(e)));

/**
 * Sets the conversation map (used by server lifecycle).
 * @param {Map<string, ConversationEntry>|null} map
 */
const setConversationMap = (map) => {
    conversationMap = map;
};

// ============================================================================
// Message Matching
// ============================================================================

/**
 * Common phrases that should trigger the welcome conversation.
 * These are greetings or help requests that indicate the user wants an introduction.
 */
const WELCOME_PHRASES = [
    'hi',
    'hello',
    'hola',
    'hey',
    'what can you help me with',
    'what can you help me with?',
    'what can you do',
    'what can you do?',
    'help'
];

/**
 * Finds the best matching conversation entry for a given user message.
 * Used to identify which recorded conversation to start.
 * Matching strategy (in order of priority):
 *   0. Welcome phrases — triggers welcome.json for greetings/help requests
 *   1. Exact match (case-insensitive)
 *   2. Recorded prompt contained within user message, or vice versa
 *   3. Word-overlap similarity — picks the recording with the most shared words
 *   4. Falls back to the first registered conversation
 *
 * @param {string} userMessage - The user's incoming message.
 * @returns {ConversationEntry|null} - The matched conversation entry.
 */
const findConversation = (userMessage) => {
    if (!conversationMap || conversationMap.size === 0) {
        console.warn('AI Chat: No conversations loaded');
        return null;
    }

    const normalizedInput = userMessage.toLowerCase().trim();

    // 0. Check for welcome phrases
    if (WELCOME_PHRASES.includes(normalizedInput)) {
        const welcomeConv = FALLBACK_KEY && conversationMap.get(FALLBACK_KEY);
        if (welcomeConv) {
            console.log('AI Chat: Welcome phrase detected, using welcome conversation');
            return welcomeConv;
        }
    }

    // 1. Exact match
    if (conversationMap.has(normalizedInput)) {
        console.log('AI Chat: Exact match found');
        return conversationMap.get(normalizedInput);
    }

    // 2. Substring match
    for (const [prompt, entry] of conversationMap) {
        if (normalizedInput.includes(prompt) || prompt.includes(normalizedInput)) {
            console.log(`AI Chat: Substring match — "${prompt.substring(0, 50)}..."`);
            return entry;
        }
    }

    // 3. Word-overlap similarity
    const inputWords = new Set(normalizedInput.split(/\s+/));
    let bestMatch = null;
    let bestScore = 0;

    for (const [prompt, entry] of conversationMap) {
        const promptWords = prompt.split(/\s+/);
        const score = promptWords.filter(word => inputWords.has(word)).length;
        if (score > bestScore) {
            bestScore = score;
            bestMatch = entry;
        }
    }

    if (bestMatch && bestScore > 0) {
        console.log(`AI Chat: Best word-overlap match (score: ${bestScore})`);
        return bestMatch;
    }

    // 4. Fallback — welcome conversation
    const fallback = (FALLBACK_KEY && conversationMap.get(FALLBACK_KEY)) || conversationMap.values().next().value;
    console.log('AI Chat: No match found, using fallback (welcome) conversation');
    return fallback;
};

// ============================================================================
// Replay Engine
// ============================================================================

/** Track active replay per socket to allow concurrent conversations */
let activeReplayId = 0;

/**
 * Resets the active replay ID counter (used by server lifecycle on shutdown).
 */
const resetActiveReplayId = () => {
    activeReplayId = 0;
};

/**
 * Computes the delay between consecutive chat messages based on their recorded timestamps.
 * Uses the time difference from the HAR recording, clamped to a max of 2 seconds
 * to keep the offline replay responsive.
 * @param {number} currentTime - The timestamp of the current message (seconds).
 * @param {number} nextTime - The timestamp of the next message (seconds).
 * @returns {number} - The delay in milliseconds.
 */
const getReplayDelay = (currentTime, nextTime) => {
    if (!nextTime || !currentTime) return 50;
    const diffMs = Math.max(0, (nextTime - currentTime) * 1000);
    // Clamp to max 2s to keep the demo snappy, min 20ms for rapid sequences
    return Math.min(Math.max(diffMs, 20), 2000);
};

/**
 * Replays a single turn's receive messages over the WebSocket connection
 * with realistic timing based on the original HAR timestamps.
 * @param {Object} socket - The WebSocket connection object.
 * @param {Array} receiveMessages - The server response messages for this turn.
 * @param {number} replayId - The replay session ID for cancellation support.
 * @returns {Promise<boolean>} - true if completed, false if cancelled.
 */
const replayTurn = async (socket, receiveMessages, replayId) => {
    for (let i = 0; i < receiveMessages.length; i++) {
        // If a newer replay was started, stop this one
        if (replayId !== activeReplayId) {
            console.log('AI Chat: Replay cancelled (new conversation started)');
            return false;
        }

        const message = receiveMessages[i];
        const nextMessage = receiveMessages[i + 1];

        console.log('AI Chat: Sending message:', message.data?.substring?.(0, 80) || message.data);
        socket.send(message.data);

        const delay = getReplayDelay(message.time, nextMessage?.time);
        await sleep(delay);
    }

    return true;
};

// ============================================================================
// Active Session Tracking
// ============================================================================

/**
 * Per-socket session state for multi-turn conversations.
 * Tracks which conversation is active and which turn to replay next.
 * @type {Map<Object, {conversation: ConversationEntry, currentTurnIndex: number, replayId: number}>}
 */
const socketSessions = new Map();

/**
 * Handles an incoming user message on a socket.
 * - For the first message: matches to a conversation and replays turn 0.
 * - For subsequent messages in a multi-turn conversation: advances to the next turn.
 * - If the conversation is exhausted, sends a completion message.
 *
 * @param {Object} socket - The WebSocket connection object.
 * @param {string} userContent - The user's message content.
 */
const handleUserMessage = async (socket, userContent) => {
    const session = socketSessions.get(socket);

    if (session) {
        // ── Multi-turn: advance to next turn ──
        const nextIndex = session.currentTurnIndex + 1;

        if (nextIndex >= session.conversation.turns.length) {
            console.log('AI Chat: Conversation complete — no more turns');
            socketSessions.delete(socket);
            return;
        }

        const nextTurn = session.conversation.turns[nextIndex];
        session.currentTurnIndex = nextIndex;
        const replayId = ++activeReplayId;
        session.replayId = replayId;

        console.log(`AI Chat: Multi-turn — replaying turn ${nextIndex + 1}/${session.conversation.turns.length}`);
        const completed = await replayTurn(socket, nextTurn.receiveMessages, replayId);

        // If this was the last turn, clean up the session
        if (completed && nextIndex >= session.conversation.turns.length - 1) {
            console.log('AI Chat: Multi-turn conversation fully replayed');
            socketSessions.delete(socket);
        } else if (completed && session.conversation.isMultiTurn) {
            console.log('AI Chat: Turn complete — waiting for user input...');
        }
    } else {
        // ── First message: find matching conversation ──
        const conversation = findConversation(userContent);
        if (!conversation) {
            socket.send(JSON.stringify({
                type: 'RUN_ERROR',
                timestamp: Date.now(),
                data: { message: 'No recorded conversation matches this prompt', code: 'NO_MATCH' }
            }));
            return;
        }

        const replayId = ++activeReplayId;

        // Start session tracking for multi-turn
        const newSession = {
            conversation,
            currentTurnIndex: 0,
            replayId
        };
        socketSessions.set(socket, newSession);

        const firstTurn = conversation.turns[0];
        console.log(
            `AI Chat: Starting conversation — "${conversation.initialPrompt.substring(0, 50)}..." ` +
            `(turn 1/${conversation.turns.length})`
        );

        const completed = await replayTurn(socket, firstTurn.receiveMessages, replayId);

        if (completed && !conversation.isMultiTurn) {
            // Single-turn — done
            console.log('AI Chat: Single-turn conversation replayed');
            socketSessions.delete(socket);
        } else if (completed) {
            console.log('AI Chat: Turn 1 complete — waiting for user input...');
        }
    }
};

export {
    buildConversationMap,
    setConversationMap,
    resetActiveReplayId,
    socketSessions,
    handleUserMessage
};
